import {
    MARK_ALT_DEAL_STRUCTURES_STALE,
    MARK_ALT_DEAL_STRUCTURES_FRESH,
    ALT_DEAL_STRUCTURE_SCENARIO_CREATED
} from '../actionTypes/altDealStructureActionTypes';
import { nanoid } from 'nanoid';
import { scopedStateSelectors, scopedActionsCreators, scopedActions } from '@makemydeal/shared-scoped-store';
import * as offerReduxActionCreators from '../actions/offerRedux';
import * as offerReduxSelectors from '../selectors/offerRedux';
import { updateDownPayment } from '../actions/paymentInput';
import { RESIDUAL_DOLLAR } from '@makemydeal/dr-platform-types';
import type { AltDealStructureGridCell } from '@makemydeal/dr-platform-types';

/**
 * Finds the populated lease term record matching the selected cell (by months + requestedDownPayment,
 * the same stable key selectLeaseSlots uses) and returns its dollar residuals. Used by the UPQ
 * fallback: a UPQ lease payment requires base/program/adjusted residuals (hasRequiredFieldsForUPQ),
 * so we re-inject the chosen cell's residuals so the scenario payment matches the slideout cell.
 */
const getLeaseResidualsForScenario = (
    state: any,
    term: number,
    downPayment: number
): { base: number; program: number; adjusted: number } | undefined => {
    const recordsByKey = offerReduxSelectors.getLeaseRecordsByTermAndDownPayment(offerReduxSelectors.getOffer(state));
    const rec = recordsByKey.get(offerReduxSelectors.leaseRecordKey(term, downPayment)) as {
        baseResidualDollar?: number;
        programResidual?: number;
        adjustedResidualValue?: number;
    };
    const { baseResidualDollar, programResidual, adjustedResidualValue } = rec ?? {};
    if (baseResidualDollar != null && programResidual != null && adjustedResidualValue != null) {
        return { base: baseResidualDollar, program: programResidual, adjusted: adjustedResidualValue };
    }
    return undefined;
};

export const markAltDealStructuresStale = () => ({
    type: MARK_ALT_DEAL_STRUCTURES_STALE
});

export const markAltDealStructuresFresh = () => ({
    type: MARK_ALT_DEAL_STRUCTURES_FRESH
});

export const applyAlternativeDealStructure = (cell: AltDealStructureGridCell) => (dispatch: any, getState: any) => {
    const state = getState();
    const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(state);

    const newScopeId = nanoid();
    dispatch(
        scopedActionsCreators.cloneScope(
            primaryScopeId,
            {
                source: 'alternativeDealStructure',
                cellId: cell.id,
                lender: cell.lender
            },
            newScopeId
        )
    );

    dispatch(scopedActionsCreators.initializeScopes([newScopeId]));

    const { term, downPayment, apr, buyRate, dealType } = cell;
    const termId = `${dealType}_term_${term}`;
    const scoped = (action: any) => scopedActions.createScopedAction(newScopeId, action);

    dispatch(scoped(offerReduxActionCreators.updatedOfferType(dealType, { payment: false })));
    dispatch(scoped(offerReduxActionCreators.selectedTerm(termId, { payment: false })));

    if (dealType === 'lease') {
        // The new scenario inherits the deal's lease grid toggle. If the main lease grid was toggled
        // on, the cloned scenario would auto-display the grid and auto re-pick a default cell,
        // clobbering the selected term/down. Close it. Done before any residual/rate overrides below so
        // its reset (which drops overrides only in UPQ mode) can't wipe them.
        dispatch(scoped(offerReduxActionCreators.updatePaymentGridToggle(false)));

        const isDealerForcedUpq = offerReduxSelectors.getDealerForcedUpq(state);

        if (isDealerForcedUpq) {
            // PQ unavailable — the dealer is forced to UPQ (enableAutoSelectProgramAdminSetting gate),
            // so the scenario can't recompute from an auto-selected program. Re-inject the chosen cell's
            // rate + residuals as UPQ overrides so the payment matches the slideout cell instead of
            // coming back invalid. updateDownPayment fires the single UPQ recompute, by which point rate
            // + residuals are in place.
            const residuals = getLeaseResidualsForScenario(state, term, downPayment);
            if (residuals) {
                dispatch(
                    scoped(
                        offerReduxActionCreators.updateBaseResidualOverride(
                            { value: residuals.base, months: term, unitType: RESIDUAL_DOLLAR },
                            false
                        )
                    )
                );
                dispatch(
                    scoped(
                        offerReduxActionCreators.updateProgramResidualOverride(
                            { value: residuals.program, months: term, unitType: RESIDUAL_DOLLAR },
                            false
                        )
                    )
                );
                dispatch(
                    scoped(
                        offerReduxActionCreators.updateAdjustedResidualOverride(
                            { value: residuals.adjusted, months: term, unitType: RESIDUAL_DOLLAR },
                            false
                        )
                    )
                );
            }
            if (cell.moneyFactor != null) {
                dispatch(
                    scoped(
                        offerReduxActionCreators.updateSellRateOverride(
                            cell.moneyFactor,
                            term,
                            { isSelectedTerm: true, skipPaymentCall: true },
                            dealType
                        )
                    )
                );
            }
            dispatch(scoped(updateDownPayment(downPayment)));
        } else {
            // PQ available — the cloned scope carries the slideout's PQ endpoint + auto-selected program,
            // so PS recomputes the chosen term/down with the FULL program data (MF, residual, acq fee,
            // fees, incentives) and matches the slideout cell exactly. No overrides needed (the program
            // provides them); updateDownPayment (meta.payment = true) fires the single recompute.
            dispatch(scoped(updateDownPayment(downPayment)));
        }
    } else {
        // Set the cell's sell rate first WITHOUT firing a payment call, then let updateDownPayment
        // fire the single recompute — by which point the rate override is already in place.
        dispatch(
            scoped(
                offerReduxActionCreators.updateSellRateOverride(
                    apr,
                    term,
                    { isSelectedTerm: true, skipPaymentCall: true },
                    dealType
                )
            )
        );
        // Carry the cell's buy rate so the scenario's dealer reserve/profit matches the cell.
        // withPayment:false — the single updateDownPayment recompute below applies both rate overrides.
        if (buyRate != null) {
            dispatch(scoped(offerReduxActionCreators.updateBuyRateOverride(buyRate, term, { withPayment: false })));
        }
        dispatch(scoped(updateDownPayment(downPayment)));
    }

    dispatch(scopedActionsCreators.setFocusedScopeId(newScopeId));

    dispatch({
        type: ALT_DEAL_STRUCTURE_SCENARIO_CREATED,
        payload: { newScopeId, cellId: cell.id, dealType, term, downPayment, lender: cell.lender },
        meta: {
            middleware: {
                analytics: {
                    eventName: 'DR_ALT_DEAL_STRUCTURE_SCENARIO_CREATED'
                }
            }
        }
    });
};
